import { ApolloError } from '@apollo/client';
import { Category } from '@oolio-group/domain';
import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react/hooks';
import { getItem, setItem } from '../storage/interface';
import { GET_CATEGORIES_QUERY } from '../graphql/category';
import { CATEGORIES_STORAGE_KEY } from '../common/constants';
import { noopHandler } from '../utils/general';

export interface UseCategoriesProps {
  loading: boolean;
  error: string | undefined;
  categories: Category[];
  getCategories: () => void;
}

export function useCategories(): UseCategoriesProps {
  const [categories, setCategories] = useState<Category[]>([]);

  const getExistingLocalCategories = async () => {
    const categories =
      ((await getItem(CATEGORIES_STORAGE_KEY)) as Category[]) || [];
    setCategories(categories);
    return categories;
  };

  const [getCategories, getCategoriesResponse] = useLazyQuery(
    GET_CATEGORIES_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      onCompleted: data => {
        if (data?.categories) {
          setCategories(data?.categories);
          setItem(CATEGORIES_STORAGE_KEY, data.categories);
        }
      },
      onError: noopHandler,
    },
  );

  useEffect(() => {
    const fetchCategoriesIfNotCache = async () => {
      const cats = await getExistingLocalCategories();
      if (!cats || !cats.length) {
        getCategories();
      }
    };
    fetchCategoriesIfNotCache();
  }, [getCategories]);

  const loading: boolean = getCategoriesResponse.loading;
  const error: ApolloError | undefined = getCategoriesResponse.error;

  return useMemo(
    () => ({
      loading,
      error: error ? (error as ApolloError)?.message : '',
      categories,
      getCategories,
    }),
    [loading, error, categories, getCategories],
  );
}
